import { Button, Popconfirm, Tag, Tooltip, message } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { usersApi } from '../../api/users';
import type { PanelUser } from '../../types';

/** E-posta adresinin doğrulanıp doğrulanmadığını gösteren küçük rozet. */
export function EpostaDogrulamaRozeti({ emailVerifiedAt }: { emailVerifiedAt?: string | null }) {
  if (emailVerifiedAt) {
    return (
      <Tooltip title={`Doğrulama: ${new Date(emailVerifiedAt).toLocaleString('tr-TR')}`}>
        <Tag color="green" style={{ marginInlineStart: 8 }}>Doğrulandı</Tag>
      </Tooltip>
    );
  }
  return (
    <Tag variant="filled" style={{ marginInlineStart: 8, color: 'rgba(0,0,0,0.45)' }}>
      Doğrulanmadı
    </Tag>
  );
}

/** E-posta ve yanındaki doğrulama rozeti; tablo sütunlarında ortak kullanılır. */
export function EpostaHucresi({ user }: { user: Pick<PanelUser, 'email' | 'emailVerifiedAt'> }) {
  return (
    <span>
      {user.email}
      <EpostaDogrulamaRozeti emailVerifiedAt={user.emailVerifiedAt} />
    </span>
  );
}

type ApiHatasi = Error & { response?: { status?: number; data?: { message?: string } } };

/**
 * "Bağlantı gönder" eylemi: onaydan sonra kullanıcıya parola bağlantısı yollar.
 * Adres doğrulanmamışsa API davet, doğrulanmışsa sıfırlama bağlantısı gönderir.
 */
export function ParolaBaglantisiButonu({
  user,
  size,
  withLabel = false,
}: {
  user: Pick<PanelUser, 'id' | 'email' | 'isActive'>;
  size?: 'small' | 'middle';
  withLabel?: boolean;
}) {
  const mutation = useMutation({
    mutationFn: () => usersApi.sendPasswordLink(user.id),
    onSuccess: (data) => {
      message.success(data.message || 'Bağlantı gönderildi');
    },
    onError: (error: ApiHatasi) => {
      message.error(error.response?.data?.message || 'Bağlantı gönderilemedi');
    },
  });

  const devreDisi = !user.isActive;
  const buton = (
    <Button
      type={withLabel ? 'default' : 'text'}
      size={size}
      icon={<MailOutlined />}
      disabled={devreDisi}
      loading={mutation.isPending}
      aria-label="Bağlantı gönder"
    >
      {withLabel ? 'Bağlantı gönder' : null}
    </Button>
  );

  if (devreDisi) {
    return (
      <Tooltip title="Pasif kullanıcıya bağlantı gönderilemez">
        <span>{buton}</span>
      </Tooltip>
    );
  }

  return (
    <Popconfirm
      title="Parola bağlantısı"
      description={
        <div style={{ maxWidth: 320 }}>
          {user.email} adresine parola bağlantısı gönderilsin mi? Adres doğrulanmamışsa
          davet (7 gün), doğrulanmışsa sıfırlama (1 saat) bağlantısı gider.
        </div>
      }
      onConfirm={() => mutation.mutate()}
      okText="Gönder"
      cancelText="Vazgeç"
    >
      {withLabel ? buton : <Tooltip title="Bağlantı gönder">{buton}</Tooltip>}
    </Popconfirm>
  );
}
